/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * NotificationPanel - Notification Dropdown Component
 * ============================================================================
 *
 * Dropdown panel showing attention items and recent activity.
 * Supports dynamic notification types from extensible notification providers.
 *
 * ============================================================================
 */

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  X,
  FileText,
  FileWarning,
  AlertCircle,
  Wallet,
  Clock,
  Building2,
  CreditCard,
  UserPlus,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Bell,
  Loader2,
  Ban,
  Unlock,
  Shield,
  CheckCircle,
  Activity,
  PackageX,
  CalendarClock,
  ArrowLeftRight,
  LucideIcon,
} from 'lucide-react';
import { NotificationItem } from '@/types/notification.types';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  attentionItems: NotificationItem[];
  activityItems: NotificationItem[];
  totalCount: number;
  isLoading?: boolean;
}

/**
 * Map icon names from backend to Lucide components.
 * Extend this map when adding new notification types.
 */
const iconMap: Record<string, LucideIcon> = {
  FileText,
  FileWarning,
  AlertCircle,
  Wallet,
  Clock,
  Building2,
  CreditCard,
  UserPlus,
  AlertTriangle,
  CheckCircle2,
  Bell,
  Ban,
  Unlock,
  Shield,
  CheckCircle,
  Activity,
  PackageX,
  CalendarClock,
  ArrowLeftRight,
};

/**
 * Get Lucide icon component from name string.
 * Falls back to Activity icon if not found.
 */
function getIcon(iconName?: string): LucideIcon {
  if (!iconName) return Activity;
  return iconMap[iconName] || Activity;
}

/**
 * Format relative time from ISO string.
 */
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function NotificationPanel({
  isOpen,
  onClose,
  attentionItems,
  activityItems,
  totalCount,
  isLoading = false,
}: NotificationPanelProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);
  const userRole = useAuthStore((s) => s.user?.role);
  const dashboardRoute = userRole === 'SUPER_ADMIN' ? '/superadmin' : '/venta/sales-dashboard';

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('.notification-btn')) {
          onClose();
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  /**
   * Handle click on attention item.
   * Uses route from backend if available.
   */
  const handleAttentionClick = (item: NotificationItem) => {
    if (item.route) {
      navigate(item.route);
      onClose();
    }
  };

  /**
   * Handle click on activity item.
   * Uses route from backend if available.
   */
  const handleActivityClick = (item: NotificationItem) => {
    if (item.route) {
      navigate(item.route);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="notification-panel-backdrop" onClick={onClose} />
      <div ref={panelRef} className="notification-panel">
        {/* Header */}
        <div className="notification-panel-header">
          <div className="notification-panel-title">
            <Bell size={16} />
            <span>{t('notifications.title', 'Notifications')}</span>
            {totalCount > 0 && (
              <span className="notification-total-badge">{totalCount}</span>
            )}
          </div>
          <button className="notification-close-btn" onClick={onClose}>
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="notification-panel-content">
          {isLoading ? (
            <div className="notification-loading">
              <Loader2 className="animate-spin" size={24} />
              <span>{t('common.loading', 'Loading...')}</span>
            </div>
          ) : (
            <>
              {/* Attention Items Section */}
              {attentionItems.length > 0 && (
                <div className="notification-section">
                  <div className="notification-section-title">
                    <AlertCircle size={14} />
                    <span>{t('notifications.attentionRequired', 'Attention Required')}</span>
                  </div>
                  <div className="notification-items">
                    {attentionItems.map((item) => {
                      const Icon = getIcon(item.icon);
                      const color = item.color || '#6B7280';
                      return (
                        <button
                          key={item.id}
                          className={cn(
                            'notification-item',
                            'notification-item-attention',
                            item.urgency === 'HIGH' && 'urgency-high'
                          )}
                          onClick={() => handleAttentionClick(item)}
                        >
                          <div
                            className="notification-item-icon"
                            style={{ backgroundColor: `${color}20`, color }}
                          >
                            <Icon size={14} />
                          </div>
                          <div className="notification-item-content">
                            <div className="notification-item-title">
                              {t(`dashboard.attentionRequired.items.${item.title}.title`, { count: item.count })}
                              {item.count !== undefined && (
                                <span className="notification-item-count">{item.count}</span>
                              )}
                            </div>
                            <div className="notification-item-desc">
                              {t(`dashboard.attentionRequired.items.${item.title}.description`, { count: item.count, ...(item.meta || {}) })}
                            </div>
                          </div>
                          <ChevronRight size={14} className="notification-item-arrow" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Recent Activity Section */}
              {activityItems.length > 0 && (
                <div className="notification-section">
                  <div className="notification-section-title">
                    <Clock size={14} />
                    <span>{t('notifications.recentActivity', 'Recent Activity')}</span>
                  </div>
                  <div className="notification-items">
                    {activityItems.slice(0, 5).map((item) => {
                      const Icon = getIcon(item.icon);
                      const color = item.color || '#6B7280';
                      return (
                        <button
                          key={item.id}
                          className="notification-item notification-item-activity"
                          onClick={() => handleActivityClick(item)}
                        >
                          <div
                            className="notification-item-icon"
                            style={{ backgroundColor: `${color}20`, color }}
                          >
                            <Icon size={14} />
                          </div>
                          <div className="notification-item-content">
                            <div className="notification-item-title">
                              {item.entityName || item.description}
                            </div>
                            <div className="notification-item-desc">
                              {item.description} {item.createdAt && `• ${formatRelativeTime(item.createdAt)}`}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {attentionItems.length === 0 && activityItems.length === 0 && (
                <div className="notification-empty">
                  <CheckCircle2 size={32} />
                  <span>{t('notifications.allCaughtUp', 'All caught up!')}</span>
                  <span className="notification-empty-sub">
                    {t('notifications.noNewNotifications', 'No new notifications')}
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="notification-panel-footer">
          <button
            className="notification-view-all"
            onClick={() => {
              navigate(dashboardRoute);
              onClose();
            }}
          >
            {t('notifications.viewDashboard', 'View Dashboard')}
          </button>
        </div>

        <style>{notificationPanelStyles}</style>
      </div>
    </>
  );
}

const notificationPanelStyles = `
  .notification-panel-backdrop {
    position: fixed;
    inset: 0;
    z-index: 998;
  }

  .notification-panel {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 360px;
    max-height: 480px;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border);
    border-radius: 12px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
    z-index: 999;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    animation: notificationSlideIn 0.2s ease;
  }

  @keyframes notificationSlideIn {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .notification-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid var(--color-border-subtle);
    background: var(--color-bg-tertiary);
  }

  .notification-panel-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .notification-total-badge {
    background: var(--color-error, #DC2626);
    color: white;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 99px;
    min-width: 18px;
    text-align: center;
  }

  .notification-close-btn {
    padding: 4px;
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.15s ease;
  }

  .notification-close-btn:hover {
    background: var(--color-bg-hover);
    color: var(--color-text-primary);
  }

  .notification-panel-content {
    flex: 1;
    overflow-y: auto;
    padding: 8px;
  }

  .notification-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 40px 20px;
    color: var(--color-text-secondary);
    font-size: 13px;
  }

  .notification-section {
    margin-bottom: 12px;
  }

  .notification-section:last-child {
    margin-bottom: 0;
  }

  .notification-section-title {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 8px 6px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
  }

  .notification-items {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .notification-item {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 10px;
    border: none;
    background: transparent;
    border-radius: 8px;
    cursor: pointer;
    text-align: left;
    transition: all 0.15s ease;
  }

  .notification-item:hover {
    background: var(--color-bg-hover);
  }

  .notification-item-attention.urgency-high {
    background: rgba(239, 68, 68, 0.08);
  }

  .notification-item-attention.urgency-high:hover {
    background: rgba(239, 68, 68, 0.12);
  }

  .notification-item-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .notification-item-content {
    flex: 1;
    min-width: 0;
  }

  .notification-item-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 500;
    color: var(--color-text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .notification-item-count {
    background: var(--color-bg-tertiary);
    color: var(--color-text-secondary);
    font-size: 10px;
    font-weight: 700;
    padding: 1px 5px;
    border-radius: 4px;
  }

  .notification-item-desc {
    font-size: 11px;
    color: var(--color-text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 2px;
  }

  .notification-item-arrow {
    color: var(--color-text-muted);
    flex-shrink: 0;
  }

  .notification-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 40px 20px;
    color: var(--color-text-secondary);
  }

  .notification-empty svg {
    color: var(--color-success, #22C55E);
    opacity: 0.7;
  }

  .notification-empty span:first-of-type {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .notification-empty-sub {
    font-size: 12px;
    color: var(--color-text-muted);
  }

  .notification-panel-footer {
    padding: 8px 12px;
    border-top: 1px solid var(--color-border-subtle);
    background: var(--color-bg-tertiary);
  }

  .notification-view-all {
    width: 100%;
    padding: 8px;
    border: none;
    background: transparent;
    color: var(--color-brand);
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border-radius: 6px;
    transition: all 0.15s ease;
  }

  .notification-view-all:hover {
    background: rgba(139, 0, 0, 0.1);
  }

  @media (max-width: 480px) {
    .notification-panel {
      position: fixed;
      top: 52px;
      right: 8px;
      left: 8px;
      width: auto;
      max-height: calc(100vh - 70px);
    }
  }
`;

export default NotificationPanel;
